import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import api from "../api";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${(props) => (props.isDarkMode ? "#2d2d2d" : "#ffffff")};
  padding: 2rem;
  border-radius: 16px;
  max-width: 90%;
  width: 420px;
  text-align: left;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  color: ${(props) => (props.isDarkMode ? "#f7c054" : "#8747d4")};
  font-family: "Quicksand", sans-serif;
  margin: 0 0 0.75rem 0;
  font-size: 1.4rem;
  text-align: center;
`;

const Message = styled.p`
  color: ${(props) => (props.isDarkMode ? "#dddddd" : "#555555")};
  font-family: "Quicksand", sans-serif;
  font-size: 0.95rem;
  line-height: 1.45;
  margin: 0 0 1.5rem 0;
  text-align: center;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  color: ${(props) => (props.isDarkMode ? "#dddddd" : "#333333")};
  font-family: "Quicksand", sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.isDarkMode ? "#555" : "#cccccc")};
  background: ${(props) => (props.isDarkMode ? "#1f1f1f" : "#ffffff")};
  color: ${(props) => (props.isDarkMode ? "#ffffff" : "#222222")};
  font-family: "Quicksand", sans-serif;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #8747d4;
  }
`;

const ErrorText = styled.p`
  color: #dc3545;
  font-family: "Quicksand", sans-serif;
  font-size: 0.85rem;
  margin: 0 0 0.75rem 0;
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  border: none;
  transition: background 0.2s ease, opacity 0.2s ease;
`;

const PrimaryButton = styled(Button)`
  background: #8747d4;
  color: white;

  &:hover:not(:disabled) {
    background: #9859e0;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: ${(props) => (props.isDarkMode ? "#444444" : "#e9e9ef")};
  color: ${(props) => (props.isDarkMode ? "#ffffff" : "#333333")};

  &:hover:not(:disabled) {
    background: ${(props) => (props.isDarkMode ? "#555555" : "#dcdce4")};
  }
`;

const MAX_NAME_LENGTH = 40;

function DisplayNameModal({
  isOpen,
  onClose,
  currentGivenName,
  currentFamilyName,
  onSaved,
}) {
  const { isDarkMode } = useAmazonContext();
  const [givenName, setGivenName] = useState(currentGivenName || "");
  const [familyName, setFamilyName] = useState(currentFamilyName || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setGivenName(currentGivenName || "");
      setFamilyName(currentFamilyName || "");
      setError("");
      setSubmitting(false);
    }
  }, [isOpen, currentGivenName, currentFamilyName]);

  if (!isOpen) return null;

  const trimmedGiven = givenName.trim();
  const trimmedFamily = familyName.trim();
  const canSave = trimmedGiven.length > 0 && trimmedFamily.length > 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      setError("You must be signed in to change your name.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      await api.post("/updateDisplayName", {
        userEmail,
        displayGivenName: trimmedGiven,
        displayFamilyName: trimmedFamily,
      });

      if (onSaved) {
        onSaved(trimmedGiven, trimmedFamily);
      }
      onClose();
    } catch (err) {
      const serverMsg = err?.response?.data?.error;
      setError(serverMsg || "Could not update your name. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent isDarkMode={isDarkMode} onClick={(e) => e.stopPropagation()}>
        <Title isDarkMode={isDarkMode}>Change your preferred name</Title>
        <Message isDarkMode={isDarkMode}>
          This is the name that will be shown on the leaderboard, in the
          knowledge challenge, and elsewhere in the app.
        </Message>

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <Label isDarkMode={isDarkMode} htmlFor="displayGivenName">
              First name
            </Label>
            <Input
              id="displayGivenName"
              isDarkMode={isDarkMode}
              type="text"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              maxLength={MAX_NAME_LENGTH}
              required
              autoFocus
            />
          </FieldGroup>

          <FieldGroup>
            <Label isDarkMode={isDarkMode} htmlFor="displayFamilyName">
              Last name
            </Label>
            <Input
              id="displayFamilyName"
              isDarkMode={isDarkMode}
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              maxLength={MAX_NAME_LENGTH}
              required
            />
          </FieldGroup>

          {error && <ErrorText>{error}</ErrorText>}

          <ButtonRow>
            <SecondaryButton
              type="button"
              isDarkMode={isDarkMode}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={!canSave}>
              {submitting ? "Saving..." : "Save"}
            </PrimaryButton>
          </ButtonRow>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
}

export default DisplayNameModal;
